import React from "react";
import * as ReactBootstrap from "react-bootstrap";

import {BuybackEMIForm} from "./BuybackEMIForm";
import {BuybackEMISummary} from "./BuybackEMISummary";
import {useBuybackEMI} from "./hooks";
import type {BuybackEMITicketShape} from "./hooks";
import type {SaveBuybackEMIRecordPayload} from "./types";

export interface BuybackTabProps {
  /** Ticket print context — only ``url`` and ``reference_number`` are read. */
  ticket: BuybackEMITicketShape;
}

/**
 * BuybackTab
 *
 * Single-write capture for the Buyback / No Cost EMI decision tree:
 *
 *   - **Capture (form) mode** — shown when no record exists yet for the
 *     ticket. Renders the conditional step-form via :func:`BuybackEMIForm`.
 *   - **Read-only summary mode** — shown once a record exists. Renders the
 *     answers as a Q&A tree via :func:`BuybackEMISummary` with a destructive
 *     Delete action; the record itself is intentionally **not editable**, so
 *     to amend the capture the user deletes and re-captures (preserving a
 *     clean change-log trail).
 *
 * Persists via :func:`useBuybackEMI` (POST creates the record).
 */
export const BuybackTab: React.FC<BuybackTabProps> = ({ticket}) => {
  const {record, isLoading, isSaving, isDeleting, save, remove} =
    useBuybackEMI(ticket);

  const handleSave = async (
    payload: SaveBuybackEMIRecordPayload,
  ): Promise<unknown> => save(payload);

  if (isLoading && record == null) {
    return (
      <ReactBootstrap.Card className="mt-3">
        <ReactBootstrap.Card.Body className="text-center py-4">
          <ReactBootstrap.Spinner animation="border" role="status" size="sm" />
          <span className="ms-2 text-muted">Loading Buyback / EMI…</span>
        </ReactBootstrap.Card.Body>
      </ReactBootstrap.Card>
    );
  }

  // Once captured, the record is read-only (Delete-to-recapture only).
  if (record != null) {
    return (
      <div className="mt-3">
        <BuybackEMISummary
          record={record}
          isDeleting={isDeleting}
          onDelete={remove}
        />
      </div>
    );
  }

  return (
    <div className="mt-3">
      <BuybackEMIForm
        record={null}
        isLoading={isLoading}
        isSaving={isSaving}
        onSubmit={handleSave}
      />
    </div>
  );
};

export default BuybackTab;
